import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  Box,
  Badge,
  Container
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useCart } from './CartProvider';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteIcon from '@mui/icons-material/Delete';
import { NavbarHandle } from '../types';

const Navbar = forwardRef<NavbarHandle>((_, ref) => {
  const { isAdmin, handleLogout } = useAuth();
  const { cartItems, removeFromCart } = useCart();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isOverlayRoute = location.pathname === '/';

  useImperativeHandle(ref, () => ({
    openCart: () => setDrawerOpen(true),
  }));

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigateToAddProduct = () => {
    handleMenuClose();
    navigate('/admin/product');
  };

  const handleNavigateToOrders = () => {
    handleMenuClose();
    navigate('/admin/orders');
  };

  const handleNavigateToAdminConfig = () => {
    handleMenuClose();
    navigate('/admin/config');
  };

  const handleCheckout = () => {
    setDrawerOpen(false);
    navigate('/checkout');
  };

  const handleNavigateHome = () => {
    navigate('/');
  };

  return (
    <AppBar
      position={isOverlayRoute ? 'absolute' : 'static'}
      elevation={isOverlayRoute ? 0 : 4}
      sx={
        isOverlayRoute
          ? { backgroundColor: 'transparent', boxShadow: 'none' }
          : {
              backgroundImage:
                "url('https://smallsite-blw-static.s3.us-east-2.amazonaws.com/blw-static/blw2-2.jpg')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              color: 'white',
            }
      }
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h6"
              component="div"
              onClick={handleNavigateHome}
              noWrap
              sx={{
                fontWeight: 600,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                mx: 2,
              }}
            >
              Boston Laser Works
            </Typography>
          </Box>

          {isAdmin && (
            <>
              <Button
                aria-controls="admin-menu"
                aria-haspopup="true"
                onClick={handleMenuClick}
                color="inherit"
              >
                Admin
              </Button>
              <Menu
                id="admin-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleNavigateToAddProduct}>Add New Product</MenuItem>
                <MenuItem onClick={handleNavigateToOrders}>Manage Orders</MenuItem>
                <MenuItem onClick={handleNavigateToAdminConfig}>Admin Configuration</MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </>
          )}

          <IconButton color="inherit" onClick={() => setDrawerOpen(true)} sx={{ ml: 2 }}>
            <Badge badgeContent={cartItems.length} color="secondary">
              <ShoppingCartIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </Container>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 300, p: 2 }} role="presentation">
          <Typography variant="h6" gutterBottom>Your Cart</Typography>
          {cartItems.length === 0 && (
            <Typography variant="body1">Cart is empty</Typography>
          )}
          {cartItems.map((item: any, idx: any) => (
            <Box
              key={`${item.productId}-${item.variationId ?? 'null'}-${idx}`}
              sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1">{item.name}</Typography>
                <Typography variant="body2">
                  Quantity: {item.quantity} | Price: ${item.price.toFixed(2)}
                </Typography>
              </Box>
              <IconButton
                color="error"
                onClick={() => removeFromCart(item.productId, item.variationId)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          {cartItems.length > 0 && (
            <Button variant="contained" color="primary" onClick={handleCheckout} sx={{ mt: 2 }}>
              Checkout
            </Button>
          )}
        </Box>
      </Drawer>
    </AppBar>
  );
});

export default Navbar;
